import { supabase } from '@/integrations/supabase/client';
import { toast } from "@/components/ui/sonner";

// This file will contain centralized Supabase calls for storage operations (e.g., file uploads).

export type UploadResult = {
  url: string;
  error?: string;
};

export type UploadOptions = {
  bucket: string;
  path: string;
  file: File;
  maxSizeMB?: number;
  allowedTypes?: string[];
};

const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];

export async function uploadFile({
  bucket,
  path,
  file,
  maxSizeMB = DEFAULT_MAX_SIZE_MB,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
}: UploadOptions): Promise<UploadResult> {
  try {
    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const error = `File size exceeds ${maxSizeMB}MB limit`;
      toast.error(error);
      return { url: "", error };
    }

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      const error = `File type not allowed. Allowed types: ${allowedTypes.join(', ')}`;
      toast.error(error);
      return { url: "", error };
    }

    // Upload file
    const { data, error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: true,
      });

    if (uploadError) {
      toast.error(`Upload failed: ${uploadError.message}`);
      return { url: "", error: uploadError.message };
    }

    // Generate a signed URL (valid for 15 minutes)
    const { data: signedData, error: signedUrlError } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 60 * 15);

    if (signedUrlError) {
      toast.error(`Failed to generate signed URL: ${signedUrlError.message}`);
      return { url: "", error: signedUrlError.message };
    }

    const signedUrl = signedData.signedUrl;
    toast.success("File uploaded and signed URL generated successfully");
    return { url: signedUrl };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
    toast.error(`Upload failed: ${errorMessage}`);
    return { url: "", error: errorMessage };
  }
}

export const uploadBase64Image = async (base64Image: string, bucket: string, path: string) => {
    const base64Data = base64Image.split(',')[1];
    if (!base64Data || !/^[A-Za-z0-9+/=]+={0,2}$/.test(base64Data)) {
      throw new Error('Invalid base64 image data');
    }
    
    let byteCharacters;
    try {
      byteCharacters = atob(base64Data);
    } catch (err) {
      throw new Error('Failed to decode base64 string');
    }

    const byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    const blob = new Blob([new Uint8Array(byteArrays)], { type: 'image/jpeg' });

    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, blob, {
        upsert: false,
        cacheControl: '3600'
      });

    if (error) {
      throw new Error(`Failed to upload photo: ${error.message}`);
    }

    return path;
}; 